import React from "react";
import {
    ArrowMaximize20Regular,
    Delete20Regular,
    Edit20Regular
} from "@fluentui/react-icons";
import { ILookupDictionary } from "../../lookup/types/ILookupDictionary";
import { ScheduledJob } from "../../redux/types/ScheduledJob";
import { Table } from "antd";
import IconTextButton from "../../repertoire/components/iconTextButton/IconTextButton";
import RepertoireModalContainer from "../../repertoire/RepertoireModalContainer";
import { EDIT_JOB_SCHEDULE_MODAL } from "../../repertoire/Consts";
import { IYesNoPromptViewModalProps } from "../../repertoire/components/modalViews/yesNoPromptView/YesNoPromptView";
import { ISubmissionPromptViewModalProps } from "../../repertoire/components/modalViews/workSubmission/WorkSubmission";
import { CronExpressionParser } from 'cron-parser';
import DateDisplay from "../../repertoire/components/dateDisplay/DateDisplay";
import ITooltip from "../../repertoire/components/Tooltip/IToolTip";
import cronstrue from "cronstrue";
import SpinnerPopUp from "../../repertoire/components/spinner/SpinnerPopUp";

export interface IScheduledJobsPageProps {
    lookupValues: ILookupDictionary;
    getAllScheduledJob: () => void;
    scheduledJobs: ScheduledJob[];
    dataGridTableData: { fields: any[] };
    getRepertoireFields: () => void;
    upsertScheduledJobAsync: (scheduledJob: ScheduledJob) => void;
    deleteScheduledJobAsync: (scheduledJob: ScheduledJob) => void;
    showModal: (modalContent: string, modalComponentInstance: string, modalProps: any, displayModalCloseButton: boolean, title: string) => void;
    displayModal: boolean;
    hideModal: () => void;
    showYesNoPrompt: (title: string, props: IYesNoPromptViewModalProps, message?: string, detailMessage?: string) => void;
    showJSONCodeInCopyModal: (submission: string, props: ISubmissionPromptViewModalProps) => void;
    customerTimeZone: string;
}

export const ScheduledJobsPage: React.FC<IScheduledJobsPageProps> = ({
    lookupValues,
    getAllScheduledJob,
    scheduledJobs,
    dataGridTableData,
    getRepertoireFields,
    upsertScheduledJobAsync,
    showModal,
    displayModal,
    deleteScheduledJobAsync,
    hideModal,
    showYesNoPrompt,
    showJSONCodeInCopyModal,
    customerTimeZone
}) => {

     const [loaded, setLoaded] = React.useState(false)

     React.useEffect(() => { getAllScheduledJob(); }, []);

    React.useEffect(() => {
    if (!dataGridTableData.fields?.length) {
        getRepertoireFields?.();
    }}, [dataGridTableData.fields]);

    React.useEffect(() => {
    if (dataGridTableData.fields?.length) {
        setLoaded(true);
    }
}, [dataGridTableData.fields]);

    const handleDelete = (record: ScheduledJob) => {
        if (record) {

                const message = `Are you sure you want to delete the scheduled job: ${getJobTypeDescription(record.type)}?`
                const modalHeader = `Delete Scheduled Job: ${record.type}?`;
                const props: IYesNoPromptViewModalProps = {
                    yesCallback: () => { deleteScheduledJobAsync(record);
                      getAllScheduledJob();
                        hideModal();
                    },
                    noCallback: () => { hideModal(); }
                }
                showYesNoPrompt(modalHeader, props, undefined, message);
        }
    }

    const getJobTypeDescription = (jobType: string, isScheduledJob?: boolean) => {
        if (isScheduledJob && jobType) {
            return jobType;
        }
        const jobTypeLookup = lookupValues?.JobType?.lookups.find(x => x.code === jobType);
        return jobTypeLookup && jobType ? `${jobType} - ${jobTypeLookup.description}` : '';
    }

    const getJobType = (isScheduledJob?: boolean) => {
        if (isScheduledJob) {
            return 'System scheduled';
        }
        return 'User scheduled';
    }

    const handleOpenEditModal = (record: any) => {
        if (record) {
            showModal(EDIT_JOB_SCHEDULE_MODAL, EDIT_JOB_SCHEDULE_MODAL, record, true, 'Edit Scheduled Job');
        }
    }

    const getColumns = () => {
        let fields = dataGridTableData?.fields.filter(x => x.componentInstance == 'allJobSearchResultsView' );
        if( fields?.length == 0 ) {
            fields = dataGridTableData.fields.filter(x => x.componentInstance == 'allJobSearchResultsView');
            return []
        }

    const showNextRunDateOfJob = (record: ScheduledJob) => {
        const cronExpression = record.cronSchedule;
        if (!cronExpression) {
            return null;
        }
        try {
            const interval = CronExpressionParser.parse(cronExpression, {currentDate: record.lastRunDate, tz: customerTimeZone});
            return interval.next().toDate();
        } catch (err) {
            return null;
        }
    }

        const getCronDescription = (cronExpression?: string) => {
          if (!cronExpression) {
            return "";
          }

          try {
            return cronstrue.toString(cronExpression);
          } catch (err) {
            return "";
          }
        }

    const onOpenCodeModel = (submission: string, type: string) => {
            const field = JSON.stringify(JSON.parse(submission), null, 2)
            const props: ISubmissionPromptViewModalProps = {
                hideCallback: () => { hideModal() },
                field: field === 'null' ? '' : field,
                title: type
            }
            showJSONCodeInCopyModal(submission, props);
        }

        const columns: any = [
        {
            title: fields[0].data,
            dataIndex: fields[0].name,
            key: `${fields[0].name}${0}`,
            render: (_, record: ScheduledJob) => {
              return <>{getJobType(record.isScheduledJob)}</>;
            },
            width: '10%',
          },
                  {
            title: fields[1].data,
            dataIndex: fields[1].name,
            key: `${fields[0].name}${1}`,
            render: (_, record: ScheduledJob) => {
              return record.type ? (<>{getJobTypeDescription(record.type, record.isScheduledJob)}</>) : <></>;
            },
            width: '10%',
          },
          {
            title: fields[2].data,
            dataIndex: fields[2].name,
            key: `${fields[0].name}${2}`,
            ellipsis: true,
            render: (_, record: ScheduledJob) => {
              return record.parameters ? (
                <div className="tableCell hasCode flexRow flexAlignItemsCenter flexJustifyContentSpaceBetween">
                  <code
                    className="overflowEllipsis"
                    style={{ overflow: "normal",
                            whiteSpace: "normal"}}
                    title={record.parameters}
                  >
                    {record.parameters}
                  </code>
                  <IconTextButton
                    icon={ArrowMaximize20Regular}
                    text=""
                    onClick={() =>
                      onOpenCodeModel(
                        record.parameters,
                        fields[2].data,
                      )
                    }
                    disabled={false}
                  />
                </div>
              ) : <></>;
            },
            width: '15%',
          },
                  {
            title: fields[3].data,
            dataIndex: fields[3].name,
            key: `${fields[0].name}${3}`,
            render: (_, record: ScheduledJob) => {
                      if (!record.cronSchedule) {
                        return <></>;
                      }

                      const cronDescription = getCronDescription(record.cronSchedule);

                      return (
                        <ITooltip tooltipContent={cronDescription} showTooltip={!!cronDescription} placement="top">
                            <span>{record.cronSchedule}</span>
                        </ITooltip>
                      );
            },
            width: '10%',
          },

                            {
            title: fields[4].data,
            dataIndex: fields[4].name,
            key: `${fields[0].name}${4}`,
            render: (_, record: ScheduledJob) => {
              return record.lastRunDate ? (<DateDisplay value={new Date(record.lastRunDate)} showTime={true} />) : <></>;
            },
            width: '10%',
          },
                            {
            title: fields[5].data,
            dataIndex: fields[5].name,
            key: `${fields[0].name}${5}`,
            render: (_, record: ScheduledJob) => {
              return scheduledJobs.length >= 1 ? (<DateDisplay value={showNextRunDateOfJob(record)} showTime={true} />) : <></>;
            },
            width: '10%',
          },
          {
            title: fields[6].data,
            dataIndex: fields[6].name,
            key: `${fields[0].name}${7}`,
           width: '10%',
        render: (_, record: ScheduledJob) =>
              scheduledJobs.length >= 1 && !record?.isScheduledJob ? (
                <div className="table-actions-scheduled-jobs">
                <IconTextButton icon={Edit20Regular} onClick={() => handleOpenEditModal(record)} />
                  <IconTextButton icon={Delete20Regular} onClick={() => handleDelete(record)} />
             </div>) : null,
          }
        ]
        return columns.map((col) => {
            if (!col.editable) {
              return col;
            }
            return {
              ...col,
                width: col.width || undefined,
                onCell: (record: ScheduledJob) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
              }),
            };
          });
    }

    return (
        <div className="settings-page scheduled-jobs-page container-fluid pageContent">

                        {displayModal ? ( <RepertoireModalContainer  />) : null}
                        {!loaded || !scheduledJobs && (<SpinnerPopUp />)}
                                  <div className="scheduled-jobs-table">
                       {scheduledJobs && scheduledJobs.length > 0 && scheduledJobs[0].parameters && <Table
                            columns={getColumns()}
                            dataSource={scheduledJobs.map((job, index) => ({
                                          key: index,
                                          ...job,
                                        }))}
                            rowClassName={() => 'editable-row'}
                            size={'middle'}
                            tableLayout="fixed"
                            className="parent-table fullWidth"
                            pagination={{ defaultPageSize: 20 }} 
                        />}
                    </div>


        </div>
    );
}